import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import { EditTextareaField } from '../../../EditTextareaField';
import {
  type AddressData,
  GET_ACCOUNT_ADDRESSES
} from '../queries/accountAddressesQuery';

type AddressType = 'address1' | 'address2';

interface FieldMapping {
  address1ApiField: string;
  address2ApiField: string;
  formField: keyof AddressData;
}

// Field mappings for both address types
const ADDRESS_FIELDS: FieldMapping[] = [
  {
    address1ApiField: 'city',
    address2ApiField: 'address2City',
    formField: 'city'
  },
  {
    address1ApiField: 'countryRegion',
    address2ApiField: 'address2CountryRegion',
    formField: 'countryRegion'
  },
  {
    address1ApiField: 'stateProvince',
    address2ApiField: 'address2StateProvince',
    formField: 'stateProvince'
  },
  {
    address1ApiField: 'zipPostalCode',
    address2ApiField: 'address2ZipPostalCode',
    formField: 'zipPostalCode'
  },
  {
    address1ApiField: 'address1Street1',
    address2ApiField: 'address2Street1',
    formField: 'street1'
  },
  {
    address1ApiField: 'address1Street2',
    address2ApiField: 'address2Street2',
    formField: 'street2'
  },
  {
    address1ApiField: 'address1Street3',
    address2ApiField: 'address2Street3',
    formField: 'street3'
  },
  {
    address1ApiField: 'address1',
    address2ApiField: 'address2',
    formField: 'address'
  }
];

// Extra fields only for Address 1
const ADDRESS1_EXTRA_FIELDS: FieldMapping[] = [
  {
    address1ApiField: 'address1County',
    address2ApiField: '',
    formField: 'county'
  },
  {
    address1ApiField: 'fax',
    address2ApiField: '',
    formField: 'fax'
  },
  {
    address1ApiField: 'businessPhone',
    address2ApiField: '',
    formField: 'businessPhone'
  },
  {
    address1ApiField: 'address1Latitude',
    address2ApiField: '',
    formField: 'latitude'
  },
  {
    address1ApiField: 'address1Longitude',
    address2ApiField: '',
    formField: 'longitude'
  }
];

function getChangedValues(
  fields: FieldMapping[],
  addressType: AddressType,
  current: AddressData,
  initial: AddressData
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};

  for (const field of fields) {
    const apiField =
      addressType === 'address1'
        ? field.address1ApiField
        : field.address2ApiField;

    if (!apiField) continue;

    const currentVal = current[field.formField];
    const initialVal = initial[field.formField];

    if (currentVal !== initialVal) {
      changes[apiField] = currentVal === '' ? null : currentVal;
    }
  }

  return changes;
}

const UPSERT_PRIMARY_ADDRESS = gql`
  mutation UpsertPrimaryAddress(
    $accountId: ID!
    $values: PrimaryAddressUpsertInput!
  ) {
    upsertPrimaryAddress(accountId: $accountId, values: $values) {
      ok
      created
      primaryAddress {
        id
        city
        stateProvince
        zipPostalCode
        countryRegion
        address1
        address1Street1
        address1Street2
        address1Street3
        address1County
        address1Latitude
        address1Longitude
        address2
        address2Street1
        address2Street2
        address2Street3
        address2City
        address2StateProvince
        address2ZipPostalCode
        address2CountryRegion
        businessPhone
        fax
      }
    }
  }
`;

export interface EditAccountAddressDialogProps {
  accountId: string;
  addressType: AddressType;
  initialValue: AddressData;
  isOpen: boolean;
  onChange: (data: AddressData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  title: string;
  value: AddressData;
}

export function EditAccountAddressDialog({
  accountId,
  addressType,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  title,
  value
}: EditAccountAddressDialogProps) {
  const [isSaving, setIsSaving] = useState(false);
  const showExtraFields = addressType === 'address1';

  const hasAddress1Data =
    addressType === 'address1' &&
    [
      value.city,
      value.stateProvince,
      value.street1,
      value.street2,
      value.street3,
      value.zipPostalCode
    ].some(v => v.trim() !== '');
  const isCountryRegionRequired = hasAddress1Data;

  const [upsertPrimaryAddress] = useMutation(UPSERT_PRIMARY_ADDRESS, {
    refetchQueries: [{ query: GET_ACCOUNT_ADDRESSES, variables: { accountId } }]
  });

  // Get all fields to check for changes
  const fieldsToCheck = showExtraFields
    ? [...ADDRESS_FIELDS, ...ADDRESS1_EXTRA_FIELDS]
    : ADDRESS_FIELDS;

  const changes = getChangedValues(
    fieldsToCheck,
    addressType,
    value,
    initialValue
  );
  const isDirty = Object.keys(changes).length > 0;

  const handleSave = async () => {
    if (isCountryRegionRequired && !value.countryRegion.trim()) {
      toast.error('Country/Region is required when address fields are filled');
      return;
    }
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await upsertPrimaryAddress({
        variables: { accountId, values: changes }
      });

      const data = response.data as {
        upsertPrimaryAddress?: { ok?: boolean };
      };

      if (data.upsertPrimaryAddress?.ok === false) {
        toast.error('Failed to save address');
      } else {
        toast.success('Address saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving address:', error);
      toast.error('Failed to save address');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title={title}
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          label="City"
          onChange={city => {
            onChange({ ...value, city });
          }}
          value={value.city}
        />

        <EditFormField
          label="Country/Region"
          onChange={countryRegion => {
            onChange({ ...value, countryRegion });
          }}
          required={isCountryRegionRequired}
          value={value.countryRegion}
        />

        {showExtraFields && (
          <>
            <EditFormField
              label="County"
              onChange={county => {
                onChange({ ...value, county });
              }}
              value={value.county}
            />

            <EditFormField
              label="Fax"
              onChange={fax => {
                onChange({ ...value, fax });
              }}
              value={value.fax}
            />

            <EditFormField
              label="Business Phone"
              onChange={businessPhone => {
                onChange({ ...value, businessPhone });
              }}
              type="tel"
              value={value.businessPhone}
            />

            <EditFormField
              label="Latitude"
              onChange={latitude => {
                onChange({ ...value, latitude });
              }}
              value={value.latitude}
            />

            <EditFormField
              label="Longitude"
              onChange={longitude => {
                onChange({ ...value, longitude });
              }}
              value={value.longitude}
            />
          </>
        )}

        <EditFormField
          label="Street 1"
          onChange={street1 => {
            onChange({ ...value, street1 });
          }}
          value={value.street1}
        />

        <EditFormField
          label="Street 2"
          onChange={street2 => {
            onChange({ ...value, street2 });
          }}
          value={value.street2}
        />

        <EditFormField
          label="Street 3"
          onChange={street3 => {
            onChange({ ...value, street3 });
          }}
          value={value.street3}
        />

        <EditFormField
          label="ZIP/Postal Code"
          onChange={zipPostalCode => {
            onChange({ ...value, zipPostalCode });
          }}
          value={value.zipPostalCode}
        />

        <EditFormField
          label="State/Province"
          onChange={stateProvince => {
            onChange({ ...value, stateProvince });
          }}
          value={value.stateProvince}
        />

        <div className="col-span-2">
          <EditTextareaField
            label="Address"
            onChange={address => {
              onChange({ ...value, address });
            }}
            rows={4}
            value={value.address}
          />
        </div>
      </div>
    </EditDialog>
  );
}
